// components/Chatbot/MessageContent.tsx
// Renders message content with proper whitespace handling.
// V5: Inline action buttons removed; tool-based actions handled via PendingActions.
// V5.1: Confirmed action results displayed inline for chat history persistence.
// UI Phase 0: assistant messages render as markdown (user messages stay plain text).

import Markdown, { MarkdownToJSX } from 'markdown-to-jsx';
import { memo } from 'react';
import { CheckCircle, XCircle, Ban, Loader2 } from 'lucide-react';
import DebugPanel from './DebugPanel';
import { getActionIcon } from './chatUtils';
import type { ChatMessage } from '../../hooks/chat/utils';

// Raw-HTML parsing disabled: assistant output is model-generated text, never trusted markup.
const MARKDOWN_OPTIONS: MarkdownToJSX.Options = {
  disableParsingRawHTML: true,
  overrides: {
    a: { props: { target: '_blank', rel: 'noopener noreferrer', className: 'text-info underline break-words' } },
    p: { props: { className: 'my-1 first:mt-0 last:mb-0' } },
    ul: { props: { className: 'list-disc pl-5 my-1 space-y-0.5' } },
    ol: { props: { className: 'list-decimal pl-5 my-1 space-y-0.5' } },
    code: { props: { className: 'font-mono text-xs bg-gray-200 rounded px-1 py-0.5' } },
    pre: { props: { className: 'bg-gray-800 text-gray-100 rounded-md p-3 my-2 overflow-x-auto [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit' } },
    h1: { props: { className: 'text-base font-semibold mt-2 mb-1' } },
    h2: { props: { className: 'text-base font-semibold mt-2 mb-1' } },
    h3: { props: { className: 'text-sm font-semibold mt-2 mb-1' } },
    blockquote: { props: { className: 'border-l-2 border-gray-300 pl-3 my-1 text-gray-600' } },
    table: { props: { className: 'my-2 text-xs border-collapse' } },
    th: { props: { className: 'border border-gray-300 px-2 py-1 text-left font-semibold' } },
    td: { props: { className: 'border border-gray-300 px-2 py-1' } },
    hr: { props: { className: 'my-2 border-gray-200' } },
  },
};

const stripLegacyActionTags = (content: string | undefined | null): string => {
  if (!content) return content ?? '';
  return content
    .replace(/\[ACTION:\s*[^\]]+\]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

interface ExecutedActionResult {
  tool_name?: string;
  tool?: string;
  tool_use_id?: string;
  success?: boolean;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

interface BlockedActionResult {
  tool?: string;
  id?: string;
  reason?: string;
  [key: string]: unknown;
}

interface InlineConfirmedActions {
  executed?: ExecutedActionResult[];
  blocked?: BlockedActionResult[];
  [key: string]: unknown;
}

interface InlineActionResultsProps {
  confirmedActions: InlineConfirmedActions | null | undefined;
}

const InlineActionResults = ({ confirmedActions }: InlineActionResultsProps) => {
  const { executed = [], blocked = [] } = confirmedActions || {};

  const successCount = executed.filter((a) => a.success !== false).length;
  const failCount = executed.length - successCount;
  const blockedCount = blocked.length;

  if (executed.length === 0 && blockedCount === 0) {
    return null;
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
        <span className="font-medium">Actions completed:</span>
        {successCount > 0 && (
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle className="w-3 h-3" />
            {successCount}
          </span>
        )}
        {failCount > 0 && (
          <span className="flex items-center gap-1 text-red-600">
            <XCircle className="w-3 h-3" />
            {failCount}
          </span>
        )}
        {blockedCount > 0 && (
          <span className="flex items-center gap-1 text-gray-400">
            <Ban className="w-3 h-3" />
            {blockedCount}
          </span>
        )}
      </div>

      <div className="space-y-1">
        {executed.map((action, index) => {
          const Icon = getActionIcon(action.tool_name || action.tool);
          const isSuccess = action.success !== false;

          return (
            <div
              key={action.tool_use_id || `exec-${index}`}
              className={`flex items-start gap-2 text-xs rounded px-2 py-1 ${
                isSuccess ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {isSuccess ? (
                <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              )}
              <Icon className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />
              <span>{action.message || action.error || action.tool_name || action.tool}</span>
            </div>
          );
        })}

        {blocked.map((action, index) => {
          const Icon = getActionIcon(action.tool);
          return (
            <div
              key={action.id || `blocked-${index}`}
              className="flex items-start gap-2 text-xs rounded px-2 py-1 bg-gray-50 text-gray-500"
            >
              <Ban className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <Icon className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />
              <span>{action.reason || action.tool}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Why an answer stops mid-sentence.
 *
 * `wasCancelled` had one writer (the abort branch, reached from the Stop
 * button) and no reader, so a truncated answer looked exactly like a complete
 * one. The model-facing half of that — the thread being replayed to it as
 * conversation history — is handled separately, in the history builder in
 * useAgentV4Chat; this only covers what the reader sees.
 *
 * ink-2, not ink-3: this is a state notice the reader needs, and ink-3 is the
 * one ink tier index.css leaves without a measured contrast ratio (it is
 * ~3.6:1 on card, under AA). The sibling metadata line in the same bubble uses
 * ink-2 for the same reason.
 */
const InterruptedNotice = () => (
  <div className="mt-1 text-xs italic text-ink-2">
    Response interrupted — ask again to continue.
  </div>
);

export interface MessageContentProps {
  message: ChatMessage;
}

const MessageContent = ({ message }: MessageContentProps) => {
  // Waiting for the first token. A turn can spend 3-20s before any text
  // (plan refresh, intent, research rounds), so show what the backend says
  // it is doing instead of an empty card. Must come before the null return
  // below, which used to make this branch unreachable.
  if (message.isStreaming && !message.content) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-2" role="status">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        <span>{message.statusText || 'Thinking...'}</span>
      </div>
    );
  }

  // An interrupted answer with no text still has something to say. Without
  // `wasCancelled` here, returning null left the bubble that ChatMessages
  // draws unconditionally as a blank card under the student's own question,
  // with the notice below unreachable because it lives inside this component.
  // Both routes that can produce an empty interrupted message — pressing Stop
  // before the first token, and restoring one from storage — land here, so
  // this is the one place the fix belongs.
  if (!message.content && !message.confirmedActions && !message.wasCancelled) {
    return null;
  }

  const cleanContent = stripLegacyActionTags(message.content);
  const isAssistant = message.role === 'assistant';

  return (
    <div className="text-sm">
      {isAssistant ? (
        // whitespace-pre-line: markdown soft breaks (single \n in prose) must
        // still render as line breaks — the pre-markdown UI preserved them via
        // pre-wrap, and assistant output separates lines with single newlines
        // constantly. Block spacing is unaffected (markdown-to-jsx emits no
        // whitespace text nodes between sibling blocks).
        <div className="break-words whitespace-pre-line">
          {cleanContent && <Markdown options={MARKDOWN_OPTIONS}>{cleanContent}</Markdown>}
          {message.isStreaming && <span className="animate-pulse">▊</span>}
          {message.wasCancelled && <InterruptedNotice />}
        </div>
      ) : (
        <div className="whitespace-pre-wrap">
          {cleanContent}
          {message.isStreaming && <span className="animate-pulse">▊</span>}
        </div>
      )}

      {message.confirmedActions && (
        <InlineActionResults
          confirmedActions={message.confirmedActions as unknown as InlineConfirmedActions}
        />
      )}

      {message.debugEvents && message.debugEvents.length > 0 && (
        <DebugPanel events={message.debugEvents} />
      )}
    </div>
  );
};

// Memoised: every streamed chunk replaces only the streaming message object
// (useAgentV4Chat maps by id), so without this each chunk re-parsed the
// markdown of every assistant bubble in the thread.
export default memo(MessageContent);
